import { useLazyQuery, useMutation } from '@apollo/client';
import CustomizeLayoutQuiz from 'component/layout_quiz/layout_quiz';
import { useAuth } from 'contexts/AuthContext';
import { useEffect, useState, useCallback } from 'react';
import _valuesIn from 'lodash/valuesIn';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch } from 'react-redux';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { CREATE_ANSWER } from '../../../graph_ql/getQuiz/mutation';
import { GET_QUESTION_DATA, GET_QUIZ_RESPONSE } from '../../../graph_ql/getQuiz/queries';
import { PATH } from '../../../routes/constant';
import { useStyles } from '../fashion_restriction/styles';
import { useGetGender } from '../../../hooks/useGetGender';
import { EGENDER, EQuizId, ESubjectQuestion, quizContextType } from 'types/quiz';
import { setCoverDefault } from 'redux/authentication/user_detail';
import { getAnsweredQuestions } from 'utils/convertListString';
import MultipleSelectOptions from 'component/MultiSelectOptions';
import useValidateAnswers from '../hooks/useValidateAnswers';

interface IQuestionData {
    value?: string;
    id?: string;
    title?: string;
    isSelect?: boolean;
}

interface Ianswer {
    title?: string;
    value?: string;
    id?: String;
    answerId?: string;
    userProperty?: string;
    answerUpdated?: string;
    action?: string;
    isSkipped?: string;
    required?: boolean;
    options?: IQuestionData[];
    questionDataSubjectTags?: string[];
    questionDataSubject?: string;
}

interface Imutation {
    questionId: String;
    questionSubject: String;
    contextType: String;
    contextId: String;
    value: String;
    valueType: String;
    id?: String;
    questionDataIds: any[];
}

interface IProps {
    handleNext?: (value: Boolean, order?: number, gender?: EGENDER) => void;
}

const Cover = (props: IProps) => {
    const { handleNext } = props;
    const { userName } = useParams<{ userName: string }>();
    const { currentUser } = useAuth();
    const gender = useGetGender(Boolean(userName));
    const [coverQuizList, setCoverQuizList] = useState<Ianswer[]>([]);
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();
    const classes = useStyles();
    const [questionsLoading, setQuestionsLoading] = useState(false);
    const [answerLoading, setAnswerLoading] = useState(false);
    const [customValue, setCustomvalue] = useState('');
    const [customValueError, setCustomValueError] = useState(false);
    const [quizData, setQuizData] = useState<any>(null);
    const isUpdate = location.pathname.includes(PATH.MY_STYLE_PREFERENCES);
    const [answerAlreadyUpdated, setAnswerUpdated] = useState(false);
    const isValidAnswer = useValidateAnswers(
        isUpdate,
        coverQuizList?.filter((item: any) => item?.options?.filter((item) => item.isSelect).length > 0).length > 0,
        answerAlreadyUpdated,
    );

    const [getQuestionData, { loading: loadingQuestionData }] = useLazyQuery(GET_QUESTION_DATA, {
        onCompleted(data) {
            const list = coverQuizList.map((item) => {
                return {
                    ...item,
                    options:
                        data?.getQuestionData?.data?.map((questionData) => {
                            if (item?.value) {
                                let parsedValue = JSON.parse(item?.value || '');
                                const answeredCover = parsedValue?.find((i) => questionData.id === i.id);
                                if (answeredCover && answeredCover?.id === questionData?.id) {
                                    if (answeredCover.customValue) {
                                        setCustomvalue(answeredCover.customValue);
                                    }
                                    return { ...questionData, isSelect: true };
                                } else {
                                    return questionData;
                                }
                            } else {
                                return questionData;
                            }
                        }) || [],
                };
            });
            setCoverQuizList(list);
        },
    });

    useEffect(() => {
        if (coverQuizList.length) {
            let firstCoverQuiz = coverQuizList[0];
            if (!firstCoverQuiz.options?.length) {
                getQuestionData({
                    variables: {
                        input: {
                            subject: ESubjectQuestion.COVER,
                            paging: { page: 0, size: 0 },
                            enabled: true,
                        },
                    },
                });
            }
        }
    }, [coverQuizList, getQuestionData, gender]);

    const [getQuizResponse] = useLazyQuery(GET_QUIZ_RESPONSE, {
        onCompleted(data) {
            const listAnswers = _valuesIn(data.getQuizResponse?.answers);
            const listQuestions = _valuesIn(data.getQuizResponse?.questions);
            setQuizData(data.getQuizResponse);
            dispatch(
                setCoverDefault(getAnsweredQuestions(data.getQuizResponse?.answers, data.getQuizResponse?.questions)),
            );
            const list = listQuestions.map((item) => {
                const answeredCover = listAnswers.find((i) => i.questionId === item.id);
                let options: IQuestionData[] = [];
                if (coverQuizList.length) {
                    let existingCover = coverQuizList.find((i) => i.id === item.id);
                    if (!_isEmpty(existingCover) && existingCover?.options) {
                        options = existingCover?.options;
                    }
                }
                if (answeredCover) {
                    return {
                        ...item,
                        value: answeredCover.value,
                        options: options,
                        answerId: answeredCover.id,
                    };
                }
                return { ...item, options: options };
            });
            setCoverQuizList(list);
            setQuestionsLoading(false);
        },
    });

    const handleRedirection = useCallback(() => {
        if (location.pathname === PATH.QUIZ_MANAGER) {
            history.push(`${PATH.QUIZ_MANAGER}/?signup=${Date.now()}`);
        }
    }, [location, history]);

    const [answerMutation] = useMutation(CREATE_ANSWER, {
        async onCompleted(response) {
            await getQuizResponse({
                variables: {
                    input: {
                        quizId: EQuizId.COVER,
                        contextType: quizContextType,
                        contextId: EQuizId.COVER,
                        userId: currentUser.uid,
                    },
                },
            });
            setAnswerLoading(false);
            handleRedirection();
        },
        onError(error) {
            console.log('Create Failed: ', error);
        },
    });

    const createAnswer = (input: Imutation) => {
        const variables = {
            input,
        };
        answerMutation({ variables });
    };

    useEffect(() => {
        if (currentUser && Boolean(!userName) && gender) {
            setQuestionsLoading(true);
            getQuizResponse({
                variables: {
                    input: {
                        quizId: EQuizId.COVER,
                        contextType: quizContextType,
                        contextId: EQuizId.COVER,
                        userId: currentUser.uid,
                    },
                },
            });
        }
    }, [currentUser, userName, gender, getQuizResponse, handleNext]);

    const renderCoverQuiz = (slide, index) => {
        return (
            <MultipleSelectOptions
                key={index}
                slide={slide}
                loadingQuestionData={loadingQuestionData}
                handleMultiSelect={handleMultiSelect}
                customValue={customValue}
                handleTextFieldChange={handleTextFieldChange}
                customValueError={customValueError}
            />
        );
    };
    const handleMultiSelect = (option) => {
        setAnswerUpdated(true);
        let updateCoverQuizList = coverQuizList.map((item) => {
            return {
                ...item,
                options: item?.options?.map((d) => {
                    if (option.value === 'Nothing Specific') {
                        if (option.id === d.id) {
                            return { ...d, isSelect: !option.isSelect };
                        } else {
                            return { ...d, isSelect: false };
                        }
                    } else if (option.id === d.id) {
                        return { ...d, isSelect: !option.isSelect };
                    } else if (d.value === 'Nothing Specific') {
                        return { ...d, isSelect: false };
                    }
                    return d;
                }),
            };
        });
        setCoverQuizList(updateCoverQuizList);
    };

    const handleTextFieldChange = (e) => {
        setCustomvalue(e.target.value);
        if (customValueError) {
            setCustomValueError(false);
        }
    };

    const handleSaveAnswer = () => {
        if (coverQuizList.length) {
            let quiz = coverQuizList[0];
            let selectedOptionList: Ianswer[] = [];
            let isCustomValueMissing = false;
            quiz?.options?.forEach((item) => {
                if (item?.isSelect && item?.value?.toLowerCase() === 'other') {
                    if (customValue) {
                        selectedOptionList = [...selectedOptionList, item];
                    } else {
                        isCustomValueMissing = true;
                        setCustomValueError(true);
                    }
                } else if (item?.isSelect && item?.value?.toLowerCase() !== 'other') {
                    selectedOptionList = [...selectedOptionList, item];
                }
            });
            if (!isCustomValueMissing) {
                const selectedOptionValue = selectedOptionList?.map((item) => {
                    let selectedItem = {
                        id: item.id,
                        title: item.title,
                        value: item.value,
                    };
                    if (item.value === 'Other') {
                        return { ...selectedItem, type: 'custom', customValue };
                    } else {
                        return { ...selectedItem, type: 'default', customValue: '' };
                    }
                });
                const selectedOptionId = selectedOptionList?.map((item) => {
                    return item.id;
                });
                setAnswerLoading(true);
                createAnswer({
                    contextId: EQuizId.COVER,
                    contextType: quizContextType,
                    questionId: quiz?.id || '',
                    questionSubject: quiz.questionDataSubject || '',
                    value: JSON.stringify(selectedOptionValue),
                    valueType: 'OBJECT_LIST',
                    id: quiz?.answerId,
                    questionDataIds: selectedOptionId || [],
                });
            }
        }
    };

    return (
        <CustomizeLayoutQuiz
            quizId={EQuizId.COVER}
            title={Boolean(!userName) ? quizData?.subTitle : ''}
            titlePage={quizData?.title}
            handleAnswer={() => {
                handleSaveAnswer();
            }}
            isUpdate={location.pathname !== PATH.QUIZ_MANAGER}
            isDisplayContent={Boolean(!userName)}
            hideBottomNav={location.pathname === PATH.QUIZ_MANAGER}
            requiredQuestion={true}
            loading={answerLoading}
            containerLoading={questionsLoading}
            nextBtnDisabled={!isValidAnswer}
        >
            <div className={classes.container}>
                {coverQuizList.map((slide, index) => {
                    return renderCoverQuiz(slide, index);
                })}
            </div>
        </CustomizeLayoutQuiz>
    );
};
export default Cover;
